import { runConvexQuery } from '@/lib/convex';

// Frontend service wrapper for the membership-led offer architecture.
// Wraps Convex queries from the merged PR #31 (membership-offer-arch) behind a typed
// surface so the UI can consume them without knowing internal field shapes.
//
// Convex sources:
//   - convex/entitlements.ts        :: getMembershipEntitlementsForUser
//   - convex/billingState.ts        :: getSubscriptionStateForUser, getPaymentPlansForUser,
//                                       getProductLibraryItemsForUser

export type MembershipPlan = 'plus' | 'core_monthly' | 'core_annual' | 'none';

export interface MembershipEntitlements {
  core_member: boolean;
  plus_member: boolean;
  coaching_os_member: boolean;
  kickstart_buyer: boolean;
  bumps_owned: string[];
  otos_owned: string[];
  coaching_os_eligible_for_upsell: boolean;
  refund_eligible_until: string | null;
  core_access_until: string | null;
}

export interface SubscriptionState {
  _id: string;
  user_id: string;
  plan: MembershipPlan;
  plan_status: string;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean | null;
  entered_plan_at?: string | null;
  payment_plan_id?: string | null;
  refund_eligible_until?: string | null;
  build_brief_confirmation_accepted_at?: string | null;
  core_access_until?: string | null;
  source?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  stripe_payment_intent_id?: string | null;
  updated_at?: string;
  created_at?: string;
}

export type ProductLibraryItemState =
  | 'owned'
  | 'locked'
  | 'available'
  | 'coming_soon'
  | 'included'
  | 'upgrade_available'
  | 'discoverable'
  | 'archived';

export interface ProductLibraryItem {
  _id: string;
  user_id: string;
  item_id: string;
  sku: string;
  display_name: string;
  state: ProductLibraryItemState;
  entitlement_link: string;
  product_family?: string | null;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export interface PaymentPlan {
  _id: string;
  user_id: string;
  plan_id: string;
  source_plan: MembershipPlan;
  status: string;
  installments_total: number;
  installments_paid: number;
  amount_total: number;
  amount_paid?: number | null;
  currency: string;
  feature_flag_enabled: boolean;
  metadata?: Record<string, unknown> | null;
  stripe_customer_id?: string | null;
  stripe_payment_intent_id?: string | null;
  stripe_subscription_id?: string | null;
}

export interface MembershipSnapshot {
  entitlements: MembershipEntitlements;
  subscriptionState: SubscriptionState | null;
  productLibraryItems: ProductLibraryItem[];
  paymentPlans: PaymentPlan[];
}

// Centralised single-call snapshot. The caller passes the Builder's userId (Clerk
// subject) plus a token. We fan out four independent reads and rebuild a snapshot.
//
// This deliberately stays as four reads instead of a god-query so each Convex source
// remains independently reactive and queryable from other surfaces (per dashboard brief
// §12 dashboard read path decision).
export async function getMembershipSnapshot(
  userId: string,
  token?: string | null,
): Promise<MembershipSnapshot> {
  const [entitlements, subscriptionState, productLibraryItems, paymentPlans] =
    await Promise.all([
      runConvexQuery<MembershipEntitlements>(
        'entitlements:getMembershipEntitlementsForUser',
        { userId },
        token,
      ),
      runConvexQuery<SubscriptionState | null>(
        'billingState:getSubscriptionStateForUser',
        { userId },
        token,
      ),
      runConvexQuery<ProductLibraryItem[]>(
        'billingState:getProductLibraryItemsForUser',
        { userId },
        token,
      ),
      runConvexQuery<PaymentPlan[]>(
        'billingState:getPaymentPlansForUser',
        { userId },
        token,
      ),
    ]);
  if (!entitlements) {
    // Convex query couldn't resolve — return a safe empty snapshot rather than throwing.
    // Surfaces should treat null subscriptionState + zero entitlements as "no membership".
    return {
      entitlements: {
        core_member: false,
        plus_member: false,
        coaching_os_member: false,
        kickstart_buyer: false,
        bumps_owned: [],
        otos_owned: [],
        coaching_os_eligible_for_upsell: false,
        refund_eligible_until: null,
        core_access_until: null,
      },
      subscriptionState,
      productLibraryItems: productLibraryItems ?? [],
      paymentPlans: paymentPlans ?? [],
    };
  }
  return {
    entitlements,
    subscriptionState,
    productLibraryItems: productLibraryItems ?? [],
    paymentPlans: paymentPlans ?? [],
  };
}

// Individual queries exposed for surfaces that only need a slice.
export async function getMembershipEntitlements(userId: string, token?: string | null) {
  return runConvexQuery<MembershipEntitlements>(
    'entitlements:getMembershipEntitlementsForUser',
    { userId },
    token,
  );
}

export async function getSubscriptionState(userId: string, token?: string | null) {
  return runConvexQuery<SubscriptionState | null>(
    'billingState:getSubscriptionStateForUser',
    { userId },
    token,
  );
}

export async function getProductLibraryItems(userId: string, token?: string | null) {
  return runConvexQuery<ProductLibraryItem[]>(
    'billingState:getProductLibraryItemsForUser',
    { userId },
    token,
  );
}

export async function getPaymentPlans(userId: string, token?: string | null) {
  return runConvexQuery<PaymentPlan[]>(
    'billingState:getPaymentPlansForUser',
    { userId },
    token,
  );
}

// Helper — derive whether a Builder is in the active refund window.
export function isInRefundWindow(
  state: Pick<SubscriptionState, 'refund_eligible_until' | 'build_brief_confirmation_accepted_at'> | null,
  now: Date = new Date(),
): boolean {
  if (!state) return false;
  if (state.build_brief_confirmation_accepted_at) {
    const bbc = new Date(state.build_brief_confirmation_accepted_at);
    if (!Number.isNaN(bbc.getTime()) && bbc.getTime() <= now.getTime()) return false;
  }
  if (!state.refund_eligible_until) return false;
  const cutoff = new Date(state.refund_eligible_until);
  if (Number.isNaN(cutoff.getTime())) return false;
  return cutoff.getTime() > now.getTime();
}
